/** Round a monetary value to 2 decimals (cents), guarding non-finite input. */
export function round2(value: number): number {
  return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
}

function optionalFiniteAmount(value: unknown): number | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Resolve persisted estimate line amounts across current and legacy snapshots.
 *
 * Older rows may omit either `lineSubtotal` or `lineTotal`. Tax lets us recover
 * the missing side without displaying a misleading $0.00 in customer-facing
 * documents. Explicit zeroes remain authoritative; only absent/non-finite
 * values use the fallback.
 */
export function resolveEstimateLineAmounts(args: {
  lineSubtotal?: unknown;
  lineTax?: unknown;
  lineTotal?: unknown;
}): { lineSubtotal: number; lineTax: number; lineTotal: number } {
  const storedSubtotal = optionalFiniteAmount(args.lineSubtotal);
  const storedTotal = optionalFiniteAmount(args.lineTotal);
  const lineTax = optionalFiniteAmount(args.lineTax) ?? 0;
  const lineSubtotal = storedSubtotal ?? (storedTotal === null ? 0 : round2(storedTotal - lineTax));
  const lineTotal = storedTotal ?? round2(lineSubtotal + lineTax);

  return { lineSubtotal, lineTax, lineTotal };
}

/**
 * Unit price derived from cost + markup: `price = cost × (1 + markup/100)`.
 * The single source of truth when cost/markup drive the price — editing either
 * cost or markup recomputes the price through this (NOT-241).
 */
export function unitPriceFromCostMarkup(cost: number, markupPercent: number): number {
  return round2(cost * (1 + markupPercent / 100));
}

/** Markup is only mathematically defined when there is a positive cost basis. */
export function isMarkupAvailable(cost: number): boolean {
  return Number.isFinite(cost) && cost > 0;
}

/**
 * Markup back-computed from a directly-entered unit price:
 * `markup = (price/cost − 1) × 100`, floored at −100 (the backend's
 * `markup ≥ -100` rule, NOT-96). When there is no positive cost basis, markup
 * is mathematically undefined and is returned as `null` instead of being
 * misrepresented as 0%.
 */
export function markupFromCostPrice(cost: number, price: number): number | null {
  if (!isMarkupAvailable(cost)) return null;
  return Math.max(-100, round2((price / cost - 1) * 100));
}

/**
 * Reconcile the editable markup for flat-priced estimate lines.
 *
 * The customer-facing unit price is the source of truth for a FLAT line. Older
 * catalog/template/estimate snapshots can contain a positive cost basis but a
 * stale 0% markup (for example, price $10,000 + cost $5,000 + markup 0). Letting
 * that snapshot into the editor unchanged both displays the wrong margin and
 * writes it back on the next save. Derive the actual markup at the boundary for
 * the line types whose estimate editor exposes cost + markup controls.
 *
 * Non-flat strategies keep their stored markup because COST_PLUS and T&M use
 * markup as a pricing input rather than metadata derived from `basePrice`.
 *
 * AGAINST THE PRICE THE LINE SELLS AT, which is not always `basePrice`. The
 * operator's price for a document line lives in `manualPriceOverride` — that is
 * what `unitPriceOf` and the operations engine's `resolveUnitPrice` both resolve
 * first, and what the editor writes so that `basePrice` stays the pricebook's
 * original (it is the denominator of the member rate; see SPEC-line-item-model,
 * "Member pricing"). Reading `basePrice` alone reported the CATALOG's markup on
 * a line priced over it: cost $100 on a line selling at $200 read 50% because
 * the pricebook said $150. The editor's own live sync derives from the
 * effective price, so a line hydrated the other way showed one markup and then
 * jumped the moment the operator touched cost or markup.
 */
export function reconcileFlatMarkupPercent(args: {
  type: string | null | undefined;
  priceStrategy: string | null | undefined;
  basePrice: number | null | undefined;
  /**
   * The operator's price for THIS document, when the line carries one. Optional
   * so a catalog/template surface — whose `basePrice` IS the price it authors,
   * with no override to hold — can leave it out and mean it.
   */
  manualPriceOverride?: number | null | undefined;
  costBasis: number | null | undefined;
  markupPercent: number | null | undefined;
}): number {
  const storedMarkup = Number.isFinite(args.markupPercent) ? Number(args.markupPercent) : 0;
  const normalizedType = String(args.type ?? '')
    .trim()
    .toUpperCase();
  const normalizedStrategy = String(args.priceStrategy ?? 'FLAT')
    .trim()
    .toUpperCase();
  const supportsEditableMarkup = ['SERVICE', 'MATERIAL', 'EQUIPMENT'].includes(normalizedType);

  if (!supportsEditableMarkup || normalizedStrategy !== 'FLAT') return storedMarkup;

  // `?? basePrice`, never `|| basePrice`: an override of 0 is a real price a
  // chargeable line may legitimately carry, and falling through it would report
  // the catalog's markup on a line being given away.
  const effectivePrice = Number.isFinite(args.manualPriceOverride)
    ? Number(args.manualPriceOverride)
    : Number(args.basePrice ?? 0);
  const derived = markupFromCostPrice(Number(args.costBasis ?? 0), effectivePrice);
  return derived ?? storedMarkup;
}

/**
 * What the cost field should become when the primary supplier's cost moves —
 * or `null` for "leave it alone".
 *
 * A material has no unit-cost field of its own — there is no `costBasis` input
 * anywhere in the form — so the primary supplier's cost IS the item's cost, and
 * everything derived from it (the "Cost … from …" line, whether Markup is
 * offered at all, and the create DTO) reads the form value. On the edit route
 * that mirror is fed from the persisted supplier rows; this form has no item
 * yet, so its suppliers are drafts in local state and there is nothing to fetch
 * — which is how the mirror came to be stuck at 0 while the table beside it
 * plainly showed $20.00, leaving Markup disabled for the whole of creation.
 *
 * A material or equipment item no longer has an independently authored cost.
 * Its primary supplier owns the cost; without one, its effective cost is zero.
 * `costBasis` remains the cached value consumed by pricing and reporting, but
 * the supplier relationship is the only place an operator can change it.
 *
 * Extracted so the rule can be asserted without standing up the route.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function costBasisFromPrimarySupplier({
  isMaterial,
  primaryCost,
  currentCostBasis,
}: {
  isMaterial: boolean;
  primaryCost: number | undefined;
  currentCostBasis: number;
}): number | null {
  // Only a material or equipment carries a supplier cost. Other types have
  // theirs cleared by `handleTypeChange`, and must not have it written back.
  if (!isMaterial) return null;

  const effectiveCost = primaryCost ?? 0;
  return currentCostBasis === effectiveCost ? null : effectiveCost;
}

/**
 * What the price and markup fields do when the MIRRORED COST moves.
 *
 * Supplier cost and catalog price are separate facts. A cost arriving on its
 * own leaves an empty price empty. An explicitly authored non-zero markup is a
 * pricing instruction, so its derived price follows supplier-cost changes; an
 * explicitly authored price stands, and its displayed markup is re-derived.
 *
 * And like the cost mirror, a write must be REVERSIBLE: a price this rule
 * derived must not outlive the cost behind it. When the cost goes away, a
 * still-derived price goes back to 0; an authored one is the operator's and
 * stays.
 *
 * Extracted so the rule can be asserted without standing up the route.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function pricingAfterMirroredCost({
  isMaterial,
  costBasis,
  currentBasePrice,
  currentMarkupPercent,
  lastDerivedBasePrice,
}: {
  isMaterial: boolean;
  costBasis: number;
  currentBasePrice: number;
  currentMarkupPercent: number;
  /** What this rule last wrote; `null` when it has not written. */
  lastDerivedBasePrice: number | null;
}): { basePrice: number } | { markupPercent: number } | null {
  // Only a material or equipment has this supplier-cost relationship.
  if (!isMaterial) return null;

  if (!isMarkupAvailable(costBasis)) {
    // Source gone — the primary was removed, or one that states no cost was
    // promoted. Take a derived price back with it; leave an authored one.
    if (lastDerivedBasePrice !== null && currentBasePrice === lastDerivedBasePrice) {
      return { basePrice: 0 };
    }
    return null;
  }

  const priceAuthored = currentBasePrice !== 0 && currentBasePrice !== lastDerivedBasePrice;
  if (!priceAuthored) {
    // A supplier cost is not a selling-price decision. ServiceTitan likewise
    // keeps material price separate from vendor cost: cost changes do not
    // automatically update price. Only an explicitly authored non-zero markup
    // is an instruction to calculate a price from this cost.
    if (currentBasePrice === 0 && currentMarkupPercent === 0) return null;
    const derived = unitPriceFromCostMarkup(costBasis, currentMarkupPercent);
    return derived === currentBasePrice ? null : { basePrice: derived };
  }

  const derivedMarkup = markupFromCostPrice(costBasis, currentBasePrice);
  if (derivedMarkup === null || derivedMarkup === currentMarkupPercent) return null;
  return { markupPercent: derivedMarkup };
}
